'use client';

import { useMemo } from 'react';

import { SmallLotPricing } from '@ravago/shared/page-data/models/elements';
import { Address } from '@ravago/shared/page-data/models/elements/address.model';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { ColorIcon } from '@ravago/shared/radiance/components/icon/components/ColorIcon/ColorIcon';
import { Radio } from '@ravago/shared/radiance/components/radio/components/Radio/Radio';
import { RadioCard } from '@ravago/shared/radiance/components/radio/components/RadioCard/RadioCard';
import { RadioCardGroup } from '@ravago/shared/radiance/components/radio/components/RadioCardGroup/RadioCardGroup';
import { RadioGroup } from '@ravago/shared/radiance/components/radio/components/RadioGroup/RadioGroup';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { LeadTimeBadge } from '../../../../shared/lead-time-badge/lead-time-badge';
import { hasSmallLotMtsLocalLeadTime } from '../../../../../utils/lead-time';
import { DEFAULT_PRODUCER_COLOR_CODE } from '../../constants';
import { getSmallLotPricingForVariant, UniqueSmallLotColor } from '../../utils/order-now-utils';

interface Props {
   smallLotPrices: SmallLotPricing[];
   smallLotDefaultAddress?: Address;
   hasQuotes: boolean;
   hasMtsLocalLeadTime?: boolean;
   uniqueSmallLotColors: UniqueSmallLotColor[];
   colorCodesForSelectedColor: string[];
   selectedColorId: number | undefined;
   selectedProducerColorCode: string | undefined;
   leadTimeIndicators: LeadTimeIndicator[];
   leadTimeEnabled: boolean;
   onColorChange: (colorId: number) => void;
   onProducerColorCodeChange: (code: string) => void;
}

export const SmallLotColorSelector = ({
   smallLotPrices,
   smallLotDefaultAddress,
   hasQuotes,
   hasMtsLocalLeadTime = false,
   uniqueSmallLotColors,
   colorCodesForSelectedColor,
   selectedColorId,
   selectedProducerColorCode,
   leadTimeIndicators,
   leadTimeEnabled,
   onColorChange,
   onProducerColorCodeChange
}: Props) => {
   const translate = useTranslations();

   const colorValue = selectedColorId !== undefined ? selectedColorId.toString() : '';
   const variantValue = selectedProducerColorCode ?? '';

   const handleColorChange = (value: string) => {
      onColorChange(Number(value));
   };

   const colorMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled || !smallLotDefaultAddress) return {};

      return uniqueSmallLotColors.reduce((acc, { id }) => {
         const codes = new Set(
            smallLotPrices.filter((pricing) => pricing.color.id === id).map((pricing) => pricing.color.producerColorCode || DEFAULT_PRODUCER_COLOR_CODE)
         );
         acc[id] = Array.from(codes).some((code) => {
            const pricing = getSmallLotPricingForVariant(smallLotPrices, id, code);
            return pricing !== null && hasSmallLotMtsLocalLeadTime(leadTimeIndicators, pricing, smallLotDefaultAddress);
         });
         return acc;
      }, {} as Record<number, boolean>);
   }, [uniqueSmallLotColors, smallLotPrices, smallLotDefaultAddress, leadTimeIndicators, leadTimeEnabled]);
   const showColorLeadTimeIndicator = hasMtsLocalLeadTime || Object.values(colorMtsLocalMap).some(Boolean);

   const variantMtsLocalMap = useMemo(() => {
      if (!leadTimeEnabled || !smallLotDefaultAddress || selectedColorId === undefined) return {};

      return colorCodesForSelectedColor.reduce((acc, code) => {
         const pricing = getSmallLotPricingForVariant(smallLotPrices, selectedColorId, code);
         acc[code] = pricing !== null && hasSmallLotMtsLocalLeadTime(leadTimeIndicators, pricing, smallLotDefaultAddress);
         return acc;
      }, {} as Record<string, boolean>);
   }, [colorCodesForSelectedColor, selectedColorId, smallLotPrices, smallLotDefaultAddress, leadTimeIndicators, leadTimeEnabled]);
   const showVariantLeadTimeIndicator = hasMtsLocalLeadTime || Object.values(variantMtsLocalMap).some(Boolean);

   const variantItems = useMemo(() => {
      const selectedColor = uniqueSmallLotColors.find((c) => c.id === selectedColorId);
      return colorCodesForSelectedColor.map((code) => ({
         value: code,
         label: code === DEFAULT_PRODUCER_COLOR_CODE ? translate('color-codes.default', { color: selectedColor?.name ?? '' }) : code
      }));
   }, [colorCodesForSelectedColor, selectedColorId, uniqueSmallLotColors, translate]);

   return (
      <Box direction="column" gap="md">
         <Box direction="column" gap="xs">
            <Typography variant="body-highlight" color="subtle">
               {translate(hasQuotes ? 'dynamic-product-block.list-prices' : 'dynamic-product-block.list-pricing-for-other-colors')}
            </Typography>
            <RadioCardGroup value={colorValue} onChange={handleColorChange}>
               {uniqueSmallLotColors.map((color) => (
                  <RadioCard key={color.id} value={color.id.toString()}>
                     <Box gap="xs" items="center" justify="between">
                        <Box gap="xs" items="center">
                           <ColorIcon colorName={color.name.toLowerCase()} />
                           <Typography>{translate(`colors.${color.name.toLowerCase().replaceAll(' ', '-')}`)}</Typography>
                        </Box>
                        {showColorLeadTimeIndicator && <LeadTimeBadge isMtsLocal={colorMtsLocalMap[color.id] ?? false} />}
                     </Box>
                  </RadioCard>
               ))}
            </RadioCardGroup>
         </Box>

         {selectedColorId !== undefined && colorCodesForSelectedColor.length > 0 && (
            <Box direction="column" gap="2xs">
               <Typography variant="body-highlight" color="subtle">
                  {translate('extended-color-picker.color-variant-label')}
               </Typography>
               <RadioGroup value={variantValue} onChange={onProducerColorCodeChange} gap="none">
                  {variantItems.map(({ value, label }) => (
                     <Box key={value} direction="row" gap="sm" justify="between" attributes={{ testid: 'small-lot-color-code-radio' }}>
                        <Radio value={value}>{label}</Radio>
                        {showVariantLeadTimeIndicator && <LeadTimeBadge isMtsLocal={variantMtsLocalMap[value] ?? false} />}
                     </Box>
                  ))}
               </RadioGroup>
            </Box>
         )}
      </Box>
   );
};
